import {
  type AskHelixInputRequestDto,
  type AskHelixInputRequestKindDto,
  type AskHelixInputRequestValueTypeDto,
} from './clarification-request';
import {
  type AskHelixValidationResult,
  validateAskHelixInputRequest,
} from './clarification-validation';
import {
  isClarificationTrustedArray,
  isClarificationTrustedRecord,
  parseClarificationTrustedData,
  type ClarificationTrustedData,
} from './clarification-trusted-parse';

export type AskHelixInputRequestSource = Readonly<{
  requestId: string;
  kind: AskHelixInputRequestKindDto;
  question: string;
  whyItMatters: string;
  expectedValueType?: AskHelixInputRequestValueTypeDto;
  unit?: string;
  options?: readonly string[];
  blocking: boolean;
  unlocksObligationIds: readonly string[];
  authority: Readonly<{
    obligationId: string;
    intentId?: string;
    requirementId?: string;
    evidenceKey?: string;
  }>;
  awaitingReview?: unknown;
  acceptedSourceClasses?: readonly string[];
}>;

export const ASK_HELIX_INPUT_REQUEST_INTERNAL_FIELDS = [
  'unlocksObligationIds',
  'authority',
  'awaitingReview',
  'acceptedSourceClasses',
] as const;

/** Explicit construction prevents new source fields from becoming public by default. */
export function projectAskHelixInputRequest(
  source: AskHelixInputRequestSource
): AskHelixInputRequestDto {
  const trusted = parseClarificationTrustedData(source);
  if (!trusted.ok) return rejectedProjection();
  return projectTrustedAskHelixInputRequest(trusted.value);
}

export function projectAskHelixInputRequests(
  sources: readonly AskHelixInputRequestSource[]
): readonly AskHelixInputRequestDto[] {
  const trusted = parseClarificationTrustedData(sources);
  if (!trusted.ok || !isClarificationTrustedArray(trusted.value)) {
    return rejectedProjection();
  }
  const projected: AskHelixInputRequestDto[] = [];
  for (const source of trusted.value) {
    projected.push(projectTrustedAskHelixInputRequest(source));
  }
  return projected;
}

export function assertAskHelixInputRequestEgress(
  dto: AskHelixInputRequestDto
): AskHelixValidationResult<AskHelixInputRequestDto> {
  return validateAskHelixInputRequest(dto);
}

function projectTrustedAskHelixInputRequest(
  source: ClarificationTrustedData
): AskHelixInputRequestDto {
  if (!isClarificationTrustedRecord(source)) return rejectedProjection();
  const request: Record<string, unknown> = {
    requestId: source.requestId,
    kind: source.kind,
    question: source.question,
    whyItMatters: source.whyItMatters,
    blocking: source.blocking,
  };
  if (source.expectedValueType !== undefined) {
    request.expectedValueType = source.expectedValueType;
  }
  if (source.unit !== undefined) request.unit = source.unit;
  if (source.options !== undefined) request.options = source.options;
  return checkedEgressRequest(request);
}

function checkedEgressRequest(request: unknown): AskHelixInputRequestDto {
  const result = validateAskHelixInputRequest(request);
  if (!result.ok) {
    throw new TypeError(`Ask Helix input request egress rejected: ${result.reason}`);
  }
  return result.value;
}

function rejectedProjection(): never {
  throw new TypeError('Ask Helix input request egress rejected: malformed_response');
}
